import React, { Fragment, useState, useEffect } from 'react'
import { Button, Grid, Box, InputLabel, Typography } from '@mui/material'
import { AutoCompleter } from '../../../components/AutoCompleter/AutoCompleter'
import { searchWorkOrders, incorporateOrdersToWorkOrder } from '../config/api'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { Order } from '../../../interfaces/Order/Order'

interface IncorporateToWorkOrderProps {
  selectedOrderIds: number[]
  orders: Order[]
  onSuccess: (message: string) => void
  onError: (message: string) => void
  onClose: () => void
}

type OptionValue = string | number

interface Option<T extends OptionValue> {
  value: T
}

export const IncorporateToWorkOrder: React.FC<IncorporateToWorkOrderProps> = ({
  selectedOrderIds,
  orders,
  onSuccess,
  onError,
  onClose
}) => {
  const { state, callApi } = useApi()
  const [workOrderIds, setWorkOrderIds] = useState<number[]>([])
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<number>(0)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [showInvalidMessage, setShowInvalidMessage] = useState<boolean>(false)

  const isLoading = state.status === Status.Loading

  useEffect(() => {
    // Reset invalid message when work order ID changes
    if (selectedWorkOrderId > 0) {
      setShowInvalidMessage(false)
    }
  }, [selectedWorkOrderId])

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value

    if (searchValue === '') {
      setIsSearching(false)
      setSelectedWorkOrderId(0)
      return
    }

    // Check if the value exists in current list
    if (
      workOrderIds.filter((x) =>
        x.toString().startsWith(searchValue.toString())
      ).length === 0
    ) {
      setIsSearching(true)

      try {
        const response = await searchWorkOrders(searchValue)
        setWorkOrderIds(response.data)
        setIsSearching(false)
      } catch (error) {
        console.error('Error searching work orders:', error)
        setIsSearching(false)
      }
    }

    setSelectedWorkOrderId(parseInt(searchValue))
  }

  const handleSubmit = async () => {
    // Validate that selected work order exists in the list
    if (
      workOrderIds.filter(
        (x) => x.toString() === selectedWorkOrderId.toString()
      ).length === 0
    ) {
      setShowInvalidMessage(true)
      return
    }

    const workOrderData = {
      workOrderId: selectedWorkOrderId,
      workOrderAddDetails: selectedOrderIds.map(id => ({ orderId: id }))
    }

    try {
      const response = await callApi(incorporateOrdersToWorkOrder(workOrderData))
        onSuccess(
          `Se ha incorporado ${selectedOrderIds.length} pedido(s) a la orden de trabajo N° ${response.id}`
        )
        onClose()
    } catch (error) {
      onError((error as any).message || 'Error al incorporar pedidos a la orden de trabajo')
      onClose()
    }
  }

  const getOrderReferenceId = (orderId: number): string => {
      const order = orders.find((o) => o.id === orderId)
      return order ? order.referenceId.toString() : orderId.toString()
    }

  const workOrderOptions: Option<OptionValue>[] = workOrderIds.map((opt) => ({
    value: opt
  }))

  return (
    <Fragment>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          Incorporar a Orden de Trabajo
        </Typography>

        <Box mt={2} mb={3}>
          <Typography variant="body2" color="textSecondary">
            <strong>Pedidos seleccionados:</strong> {selectedOrderIds.length}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            IDs de Pedidos seleccionados:{' '}
            <strong>
              {selectedOrderIds
                .slice(0, 5)
                .map((id) => getOrderReferenceId(id))
                .join(', ')}
              {selectedOrderIds.length > 5 && '...'}
            </strong>
          </Typography>
        </Box>

        <Grid
          container
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <InputLabel id="idOT">ID:{' '}</InputLabel>
          <Grid item xs={12} md={6} sm={6}>
            <AutoCompleter
              item={workOrderOptions}
              nameList="workOrderList"
              labelList="OT"
              handleChange={handleSearchChange}
              isLoader={isSearching}
            />
          </Grid>
        </Grid>

        {showInvalidMessage && (
          <Box mt={2}>
            <Typography variant="body2" color="error">
              Debe seleccionar una orden de trabajo válida
            </Typography>
          </Box>
        )}

        <Grid container spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
          <Grid item xs={12} md={3} sm={6}>
            <Button
              type="button"
              variant="contained"
              fullWidth
              style={{
                marginTop: '10px',
                color: 'white'
              }}
              onClick={onClose}
              color="secondary"
              disabled={isLoading}
            >
              Cancelar
            </Button>
          </Grid>
          <Grid item xs={12} md={3} sm={6}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              style={{
                marginTop: '10px',
                color: 'white'
              }}
              onClick={handleSubmit}
              color="primary"
              disabled={isLoading || selectedWorkOrderId === 0}
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  )
}
